/**
 * Minimal QR Code encoder: byte mode, error-correction level M, versions 1–10
 * (up to 213 bytes at M). Enough for the enrollment payload; no dependency, ~5 KB.
 * Follows ISO/IEC 18004; structure after Nayuki's reference description.
 */

export interface QrMatrix {
  size: number;
  version: number;
  mask: number;
  /** modules[y][x] === true means dark. */
  modules: boolean[][];
}

const ECC_PER_BLOCK_M = [0, 10, 16, 26, 18, 24, 16, 18, 22, 22, 26];
const NUM_BLOCKS_M = [0, 1, 1, 1, 2, 2, 4, 4, 4, 5, 5];
export const MAX_VERSION = 10;

export function rawDataModules(ver: number): number {
  let r = (16 * ver + 128) * ver + 64;
  if (ver >= 2) {
    const n = Math.floor(ver / 7) + 2;
    r -= (25 * n - 10) * n - 55;
    if (ver >= 7) r -= 36;
  }
  return r;
}

export function dataCodewords(ver: number): number {
  return Math.floor(rawDataModules(ver) / 8) - ECC_PER_BLOCK_M[ver]! * NUM_BLOCKS_M[ver]!;
}

export function alignmentPositions(ver: number): number[] {
  if (ver === 1) return [];
  const n = Math.floor(ver / 7) + 2;
  const size = ver * 4 + 17;
  const step = Math.floor((ver * 8 + n * 3 + 5) / (n * 4 - 4)) * 2;
  const out = [6];
  for (let pos = size - 7; out.length < n; pos -= step) out.splice(1, 0, pos);
  return out;
}

// ---- GF(256) Reed–Solomon -------------------------------------------------------------
const EXP = new Uint8Array(512);
const LOG = new Uint8Array(256);
(() => {
  let x = 1;
  for (let i = 0; i < 255; i++) {
    EXP[i] = x;
    LOG[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= 0x11d;
  }
  for (let i = 255; i < 512; i++) EXP[i] = EXP[i - 255] ?? 0;
})();
export function gfMul(a: number, b: number): number {
  if (a === 0 || b === 0) return 0;
  return EXP[LOG[a]! + LOG[b]!]!;
}
function rsGenerator(degree: number): Uint8Array {
  let g = new Uint8Array([1]);
  for (let i = 0; i < degree; i++) {
    const next = new Uint8Array(g.length + 1);
    for (let j = 0; j < g.length; j++) {
      next[j] = (next[j] ?? 0) ^ (g[j] ?? 0);
      next[j + 1] = (next[j + 1] ?? 0) ^ gfMul(g[j] ?? 0, EXP[i] ?? 0);
    }
    g = next;
  }
  return g;
}
export function rsEncode(data: Uint8Array, degree: number): Uint8Array {
  const gen = rsGenerator(degree);
  const rem = new Uint8Array(degree);
  for (const b of data) {
    const factor = b ^ (rem[0] ?? 0);
    rem.copyWithin(0, 1);
    rem[degree - 1] = 0;
    for (let i = 0; i < degree; i++) rem[i] = (rem[i] ?? 0) ^ gfMul(gen[i + 1] ?? 0, factor);
  }
  return rem;
}

// ---- Bit stream ---------------------------------------------------------------------
class Bits {
  readonly bits: number[] = [];
  push(value: number, len: number): void {
    for (let i = len - 1; i >= 0; i--) this.bits.push((value >>> i) & 1);
  }
}

function chooseVersion(byteLen: number): number {
  for (let v = 1; v <= MAX_VERSION; v++) {
    const countBits = v <= 9 ? 8 : 16;
    if (4 + countBits + byteLen * 8 <= dataCodewords(v) * 8) return v;
  }
  throw new Error(`QR payload too long (${byteLen} bytes; max for version ${MAX_VERSION}/M)`);
}

export function encodeCodewords(data: Uint8Array, ver: number): Uint8Array {
  const cap = dataCodewords(ver) * 8;
  const bs = new Bits();
  bs.push(0b0100, 4);
  bs.push(data.byteLength, ver <= 9 ? 8 : 16);
  for (const b of data) bs.push(b, 8);
  bs.push(0, Math.min(4, cap - bs.bits.length));
  while (bs.bits.length % 8 !== 0) bs.bits.push(0);
  for (let pad = 0xec; bs.bits.length < cap; pad ^= 0xec ^ 0x11) bs.push(pad, 8);
  const dat = new Uint8Array(cap / 8);
  for (let i = 0; i < bs.bits.length; i++) dat[i >>> 3] = ((dat[i >>> 3] ?? 0) << 1) | (bs.bits[i] ?? 0);
  return interleave(dat, ver);
}

function interleave(data: Uint8Array, ver: number): Uint8Array {
  const numBlocks = NUM_BLOCKS_M[ver]!;
  const ecc = ECC_PER_BLOCK_M[ver]!;
  const raw = Math.floor(rawDataModules(ver) / 8);
  const numShort = numBlocks - (raw % numBlocks);
  const shortLen = Math.floor(raw / numBlocks);
  const blocks: Uint8Array[] = [];
  let k = 0;
  for (let i = 0; i < numBlocks; i++) {
    const datLen = shortLen - ecc + (i < numShort ? 0 : 1);
    const dat = data.subarray(k, k + datLen);
    k += datLen;
    const block = new Uint8Array(shortLen + 1);
    block.set(dat, 0);
    block.set(rsEncode(dat, ecc), shortLen + 1 - ecc);
    blocks.push(block);
  }
  const out: number[] = [];
  for (let i = 0; i < shortLen + 1; i++) {
    blocks.forEach((b, j) => {
      if (i !== shortLen - ecc || j >= numShort) out.push(b[i]!);
    });
  }
  return new Uint8Array(out);
}

// ---- Matrix -------------------------------------------------------------------------
export function maskBit(mask: number, x: number, y: number): boolean {
  switch (mask) {
    case 0: return (x + y) % 2 === 0;
    case 1: return y % 2 === 0;
    case 2: return x % 3 === 0;
    case 3: return (x + y) % 3 === 0;
    case 4: return (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0;
    case 5: return ((x * y) % 2) + ((x * y) % 3) === 0;
    case 6: return (((x * y) % 2) + ((x * y) % 3)) % 2 === 0;
    case 7: return (((x + y) % 2) + ((x * y) % 3)) % 2 === 0;
    default: throw new Error("bad mask");
  }
}

export function formatBits(mask: number): number {
  const data = (0b00 << 3) | mask; // level M = 00
  let rem = data;
  for (let i = 0; i < 10; i++) rem = (rem << 1) ^ ((rem >>> 9) * 0x537);
  return ((data << 10) | rem) ^ 0x5412;
}

function versionBits(ver: number): number {
  let rem = ver;
  for (let i = 0; i < 12; i++) rem = (rem << 1) ^ ((rem >>> 11) * 0x1f25);
  return (ver << 12) | rem;
}

export class QrBuilder {
  readonly size: number;
  readonly modules: boolean[][];
  readonly isFunction: boolean[][];

  constructor(readonly ver: number) {
    this.size = ver * 4 + 17;
    this.modules = Array.from({ length: this.size }, () => Array<boolean>(this.size).fill(false));
    this.isFunction = Array.from({ length: this.size }, () => Array<boolean>(this.size).fill(false));
  }

  private set(x: number, y: number, dark: boolean): void {
    this.modules[y]![x] = dark;
    this.isFunction[y]![x] = true;
  }

  drawFunctionPatterns(): void {
    const s = this.size;
    for (let i = 0; i < s; i++) {
      this.set(6, i, i % 2 === 0);
      this.set(i, 6, i % 2 === 0);
    }
    this.finder(3, 3);
    this.finder(s - 4, 3);
    this.finder(3, s - 4);
    const al = alignmentPositions(this.ver);
    for (let i = 0; i < al.length; i++) {
      for (let j = 0; j < al.length; j++) {
        if ((i === 0 && j === 0) || (i === 0 && j === al.length - 1) || (i === al.length - 1 && j === 0)) continue;
        this.alignment(al[i]!, al[j]!);
      }
    }
    this.drawFormat(0);
    this.drawVersion();
  }

  private finder(cx: number, cy: number): void {
    for (let dy = -4; dy <= 4; dy++) {
      for (let dx = -4; dx <= 4; dx++) {
        const d = Math.max(Math.abs(dx), Math.abs(dy));
        const x = cx + dx;
        const y = cy + dy;
        if (x >= 0 && x < this.size && y >= 0 && y < this.size) this.set(x, y, d !== 2 && d !== 4);
      }
    }
  }

  private alignment(cx: number, cy: number): void {
    for (let dy = -2; dy <= 2; dy++) {
      for (let dx = -2; dx <= 2; dx++) this.set(cx + dx, cy + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1);
    }
  }

  drawFormat(mask: number): void {
    const bits = formatBits(mask);
    const bit = (i: number) => ((bits >>> i) & 1) === 1;
    const s = this.size;
    for (let i = 0; i <= 5; i++) this.set(8, i, bit(i));
    this.set(8, 7, bit(6));
    this.set(8, 8, bit(7));
    this.set(7, 8, bit(8));
    for (let i = 9; i < 15; i++) this.set(14 - i, 8, bit(i));
    for (let i = 0; i < 8; i++) this.set(s - 1 - i, 8, bit(i));
    for (let i = 8; i < 15; i++) this.set(8, s - 15 + i, bit(i));
    this.set(8, s - 8, true);
  }

  private drawVersion(): void {
    if (this.ver < 7) return;
    const bits = versionBits(this.ver);
    for (let i = 0; i < 18; i++) {
      const b = ((bits >>> i) & 1) === 1;
      const a = this.size - 11 + (i % 3);
      const c = Math.floor(i / 3);
      this.set(a, c, b);
      this.set(c, a, b);
    }
  }

  /** Zig-zag placement; the same walk is used to read codewords back in tests. */
  *dataPositions(): Generator<[number, number]> {
    const s = this.size;
    for (let right = s - 1; right >= 1; right -= 2) {
      if (right === 6) right = 5;
      for (let vert = 0; vert < s; vert++) {
        for (let j = 0; j < 2; j++) {
          const x = right - j;
          const upward = ((right + 1) & 2) === 0;
          const y = upward ? s - 1 - vert : vert;
          if (!this.isFunction[y]![x]) yield [x, y];
        }
      }
    }
  }

  drawCodewords(cw: Uint8Array): void {
    let i = 0;
    for (const [x, y] of this.dataPositions()) {
      if (i < cw.length * 8) {
        this.modules[y]![x] = ((cw[i >>> 3]! >>> (7 - (i & 7))) & 1) === 1;
        i++;
      }
    }
  }

  applyMask(mask: number): void {
    for (let y = 0; y < this.size; y++) {
      for (let x = 0; x < this.size; x++) {
        if (!this.isFunction[y]![x] && maskBit(mask, x, y)) this.modules[y]![x] = !this.modules[y]![x];
      }
    }
  }

  penalty(): number {
    const m = this.modules;
    const s = this.size;
    let p = 0;
    const runs = (get: (i: number, j: number) => boolean) => {
      for (let i = 0; i < s; i++) {
        let run = 1;
        for (let j = 1; j < s; j++) {
          if (get(i, j) === get(i, j - 1)) {
            run++;
            if (run === 5) p += 3;
            else if (run > 5) p += 1;
          } else run = 1;
        }
        // finder-like pattern 1011101 with 4 light on a side
        for (let j = 0; j + 11 <= s; j++) {
          const seq = Array.from({ length: 11 }, (_, k) => get(i, j + k));
          const a = [true, false, true, true, true, false, true, false, false, false, false];
          const b = [false, false, false, false, true, false, true, true, true, false, true];
          if (seq.every((v, k) => v === a[k]) || seq.every((v, k) => v === b[k])) p += 40;
        }
      }
    };
    runs((y, x) => m[y]![x]!);
    runs((x, y) => m[y]![x]!);
    for (let y = 0; y + 1 < s; y++) {
      for (let x = 0; x + 1 < s; x++) {
        const c = m[y]![x];
        if (c === m[y]![x + 1] && c === m[y + 1]![x] && c === m[y + 1]![x + 1]) p += 3;
      }
    }
    let dark = 0;
    for (const row of m) for (const v of row) if (v) dark++;
    const k = Math.ceil(Math.abs(dark * 20 - s * s * 10) / (s * s)) - 1;
    p += k * 10;
    return p;
  }
}

export function encodeQr(text: string): QrMatrix {
  const data = new TextEncoder().encode(text);
  const ver = chooseVersion(data.byteLength);
  const cw = encodeCodewords(data, ver);
  let best: { mask: number; score: number; modules: boolean[][] } | undefined;
  for (let mask = 0; mask < 8; mask++) {
    const b = new QrBuilder(ver);
    b.drawFunctionPatterns();
    b.drawCodewords(cw);
    b.drawFormat(mask);
    b.applyMask(mask);
    const score = b.penalty();
    if (!best || score < best.score) best = { mask, score, modules: b.modules.map((r) => [...r]) };
  }
  return { size: ver * 4 + 17, version: ver, mask: best!.mask, modules: best!.modules };
}

/** SVG string with a 4-module quiet zone; scales via CSS. */
export function qrToSvg(q: QrMatrix, opts: { dark?: string; light?: string; title?: string } = {}): string {
  const quiet = 4;
  const n = q.size + quiet * 2;
  let path = "";
  for (let y = 0; y < q.size; y++) {
    for (let x = 0; x < q.size; x++) if (q.modules[y]![x]) path += `M${x + quiet} ${y + quiet}h1v1h-1z`;
  }
  const title = opts.title ? `<title>${opts.title.replace(/[<&>]/g, "")}</title>` : "";
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${n} ${n}" shape-rendering="crispEdges" role="img">${title}<rect width="${n}" height="${n}" fill="${opts.light ?? "#fff"}"/><path d="${path}" fill="${opts.dark ?? "#000"}"/></svg>`;
}
